import { PlanOptions } from '@constant'
import { PlanFeatureKeys, type PlanFeatureKey } from './featureKeys'
import type { PlanFeatureEntitlement } from './types'

/**
 * Canonical plan → feature registry for Invoice.
 * Other logic should call `canUsePlanFeature` / `isPlanFeatureLocked` with these keys.
 *
 * Enforcement notes:
 * - `show_timeline` / `show_order_status_url`: sanitize + backend enforce Professional+
 *   (packing L1 nav may show Starter hint — catalog uses real enforcement).
 * - Company / location tax IDs: Premium (`subscription.pre`); Plus verify is separate modal.
 *
 * Keep `enforceOnSave` keys in sync with backend `PlanEntitlements`.
 */
export const PLAN_FEATURE_ENTITLEMENTS: Record<
  PlanFeatureKey,
  PlanFeatureEntitlement
> = {
  // ——— Starter+ ———
  [PlanFeatureKeys.IS_ITEM_COMPARE_AT_PRICE]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_item_compare_at_price',
  },
  [PlanFeatureKeys.IS_SUBTOTAL_EX_TAX]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_subtotal_ex_tax',
  },
  [PlanFeatureKeys.IS_SUBTOTAL_INC_DISCOUNT]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_subtotal_inc_discount',
  },
  [PlanFeatureKeys.IS_ITEM_SUBTOTAL_INC_DISCOUNT]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_item_subtotal_inc_discount',
  },
  [PlanFeatureKeys.SHOW_FOOTER_TERM]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'show_footer_term',
  },
  [PlanFeatureKeys.SHOW_INVOICE_EXTRA_OPTIONS]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'show_invoice_extra_options',
  },
  [PlanFeatureKeys.SHOW_TRACKING_INFO]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'show_tracking_info',
  },
  [PlanFeatureKeys.SHOW_FULFILL_LOCATION]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'show_fulfill_location',
  },
  [PlanFeatureKeys.IS_ITEM_WEIGHT_UNIT]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_item_weight_unit',
  },
  [PlanFeatureKeys.IS_TOTAL_SHIPPING]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_total_shipping',
    notes: 'Refund LineItemTotals — refunded shipping fee.',
  },
  [PlanFeatureKeys.IS_TOTAL_SHIPPING_TAX]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'is_total_shipping_tax',
    notes: 'Refund LineItemTotals — tax on refunded shipping.',
  },
  [PlanFeatureKeys.LINE_ITEM_PADDING]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'line_item_padding',
    notes: 'Gap between thumbnail and line text (Starter+).',
  },
  [PlanFeatureKeys.LINE_ITEM_TRANSLATE]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'line_item_translate',
  },
  [PlanFeatureKeys.SUPPLIER_EXTRA_FIELDS]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'supplier_extra_fields',
  },
  [PlanFeatureKeys.TEMPLATE_CURRENCY_BILLING]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'template_currency',
    notes: 'Billing presentment currency option (not dual/all).',
  },
  [PlanFeatureKeys.SAVE_AS_TEMPLATE]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
  },
  [PlanFeatureKeys.PACKING_LOCATION_NAV]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
    notes: 'Packing slip L1 Location nav (disableOnFree).',
  },
  [PlanFeatureKeys.PACKING_TRACKING_NAV]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
    notes: 'Packing slip L1 Tracking nav (disableOnFree).',
  },
  [PlanFeatureKeys.FOOTER_NOTE]: {
    requiredPlan: PlanOptions.Starter,
    category: 'theme_setting',
    themeSettingKey: 'footer_note',
    notes: 'Footer note text — gated for new stores only (featureGate=isNewStore).',
  },

  // ——— Professional+ ———
  [PlanFeatureKeys.THANK_YOU_NOTE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'thank_you_note',
    notes: 'Thank-you note text — gated for new stores only (featureGate=isNewStore).',
  },
  [PlanFeatureKeys.SHOW_TIMELINE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_timeline',
    enforceOnSave: true,
    notes:
      'Sanitized on save for non-Pro+. Packing nav may hint Starter; enforcement is Pro+.',
  },
  [PlanFeatureKeys.TIMELINE_SEPARATE_PAGE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'timeline_separate_page',
  },
  [PlanFeatureKeys.SHOW_ORDER_STATUS_URL]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_order_status_url',
    enforceOnSave: true,
  },
  [PlanFeatureKeys.SHOW_HS_CODE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_hs_code',
  },
  [PlanFeatureKeys.SHOW_ITEM_METAFIELDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_item_metafields',
  },
  [PlanFeatureKeys.LINE_ITEM_METAFIELD_TRANSLATE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'line_item_metafield_translate',
  },
  [PlanFeatureKeys.IS_ITEM_PROPERTIES]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_item_properties',
  },
  [PlanFeatureKeys.IS_ITEM_PROPERTIES_ORDER]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_item_properties_order',
  },
  [PlanFeatureKeys.IS_PRICE_AFTER_DISCOUNT]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_price_after_discount',
  },
  [PlanFeatureKeys.SHOW_VARIANT_METAFIELDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_variant_metafields',
  },
  [PlanFeatureKeys.SHOW_USED_GIFT_CARDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_used_gift_cards',
  },
  [PlanFeatureKeys.SHOW_GIFT_CARD_CODE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_gift_card_code',
  },
  [PlanFeatureKeys.IS_TOTAL_REFUND]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_total_refund',
  },
  [PlanFeatureKeys.SHOW_TAX_LINE]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_tax_line',
  },
  [PlanFeatureKeys.SHOW_PAYMENT_TERMS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_payment_terms',
  },
  [PlanFeatureKeys.SHOW_PO_NUMBER]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_po_number',
  },
  [PlanFeatureKeys.SHOW_ORDER_METAFIELDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_order_metafields',
    notes:
      'Overview: featureGate=isNewStore — old stores keep unlock; new stores lock Professional.',
  },
  [PlanFeatureKeys.SHOW_CUSTOMER_METAFIELDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_customer_metafields',
  },
  [PlanFeatureKeys.SHOW_SUPPLIER_EXTRA]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'show_supplier_extra',
  },
  [PlanFeatureKeys.IS_TAX_NUMBER]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_tax_number',
  },
  [PlanFeatureKeys.IS_ITEM_SUBTOTAL_EX_TAX]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_item_subtotal_ex_tax',
  },
  [PlanFeatureKeys.IS_NET_SHIPPING]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'is_net_shipping',
  },
  [PlanFeatureKeys.INVOICE_EXTRA_OPTIONS_FIELDS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'invoice_extra_options_fields',
  },
  [PlanFeatureKeys.CUSTOM_FIELDS_SECTION]: {
    requiredPlan: PlanOptions.Professional,
    category: 'app_feature',
    notes: 'CustomFields Form section TextPlan Professional.',
  },
  [PlanFeatureKeys.PAGE_ATTACHMENTS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'app_feature',
    notes: 'DocumentSettings HTML/URL page attachments.',
  },
  [PlanFeatureKeys.PACKING_TIMELINE_NAV]: {
    requiredPlan: PlanOptions.Professional,
    category: 'app_feature',
    notes: 'Aligned with show_timeline Pro+ enforcement (not Starter nav hint).',
  },
  [PlanFeatureKeys.CUSTOM_CSS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'theme_setting',
    themeSettingKey: 'custom_css',
    enforceOnSave: true,
    notes:
      'L1 Custom CSS editor. String setting — blanked on save below Professional (not forced to false).',
  },

  // ——— Premium ———
  [PlanFeatureKeys.SHOW_IN_STOCK]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'show_in_stock',
  },
  [PlanFeatureKeys.IS_REFUND_NO]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'is_refund_no',
  },
  [PlanFeatureKeys.IS_BILLING_COMPANY_ID]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'is_billing_company_id',
    notes: 'Premium lock + ModalVerifiedShopifyPlus on enable — not SHOPIFY_PLUS TextPlan.',
  },
  [PlanFeatureKeys.IS_SHIPPING_COMPANY_ID]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'is_shipping_company_id',
    notes: 'Premium lock + ModalVerifiedShopifyPlus on enable.',
  },
  [PlanFeatureKeys.IS_BILLING_LOCATION_TAX_ID]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'is_billing_location_tax_id',
    notes: 'Premium lock + ModalVerifiedShopifyPlus on enable.',
  },
  [PlanFeatureKeys.IS_SHIPPING_LOCATION_TAX_ID]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'is_shipping_location_tax_id',
    notes: 'Premium lock + ModalVerifiedShopifyPlus on enable.',
  },
  [PlanFeatureKeys.TEMPLATE_CURRENCY_ALL]: {
    requiredPlan: PlanOptions.Premium,
    category: 'theme_setting',
    themeSettingKey: 'template_currency',
    notes: 'Dual/all presentment currency option requires Premium.',
  },

  // ——— App features ———
  [PlanFeatureKeys.APP_AUTOMATION_EMAILS]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
    notes: 'ShopPolicy#from_pro? (= Starter+).',
  },
  [PlanFeatureKeys.APP_CUSTOM_SENDER_EMAIL]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_SHOPIFY_FLOW]: {
    requiredPlan: PlanOptions.Starter,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_EMAIL_BCC_CC]: {
    requiredPlan: PlanOptions.Professional,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_CUSTOM_INVOICE_FLAGS]: {
    requiredPlan: PlanOptions.Professional,
    category: 'app_feature',
    notes: 'enable_custom_invoice / custom_pos_invoice / enable_custom_refund.',
  },
  [PlanFeatureKeys.APP_EDIT_THEME_CODE]: {
    requiredPlan: PlanOptions.Premium,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_BULK_PDF_DOWNLOAD]: {
    requiredPlan: PlanOptions.Premium,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_DUAL_CURRENCY_LIQUID]: {
    requiredPlan: PlanOptions.Premium,
    category: 'app_feature',
  },
  [PlanFeatureKeys.APP_VAT_AUTO]: {
    requiredPlan: PlanOptions.Premium,
    category: 'app_feature',
  },
}
